import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Warning, Plus, X, FileText, Trash } from "@phosphor-icons/react";

const KIND_LABEL = {
  coi_general_liability: "COI · General Liability",
  coi_auto: "COI · Auto",
  coi_workers_comp: "COI · Workers Comp",
  w9: "W-9",
  license: "License",
  certification: "Certification",
  other: "Other",
};

const STATUS_BADGE = {
  compliant: "bg-emerald-500 text-white",
  expiring_soon: "bg-amber-500 text-white",
  expired: "bg-red-600 text-white",
  unknown: "bg-slate-300 text-slate-800",
};

export default function VendorComplianceVault() {
  const [summary, setSummary] = useState(null);
  const [docs, setDocs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selected, setSelected] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    setBusy(true);
    try {
      const [s, v, d] = await Promise.all([
        api.get("/vendor-compliance/summary"),
        api.get("/sub-contractors"),
        api.get("/vendor-compliance/docs"),
      ]);
      setSummary(s.data);
      setVendors(v.data || []);
      setDocs(d.data || []);
    } catch { toast.error("Failed to load vendor compliance"); }
    finally { setBusy(false); }
  };
  useEffect(() => { loadAll(); }, []);

  const filtered = selected ? docs.filter(d => d.vendor_id === selected) : docs;

  const del = async (did) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/vendor-compliance/docs/${did}`); toast.success("Deleted"); loadAll(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6 space-y-4" data-testid="vendor-vault-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl font-black uppercase tracking-widest flex items-center gap-2">
              <ShieldCheck weight="fill" className="text-cyan-500"/> Vendor COI / W-9 Vault
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              Central archive of insurance certs, W-9s and licenses for every sub. Risk-scored so you never dispatch an expired vendor.
            </p>
          </div>
          <button onClick={() => setShowNew(true)} data-testid="new-doc-btn"
            className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[11px] shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-1">
            <Plus size={14}/> Upload Document
          </button>
        </div>

        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="vault-stats">
            <StatCard label="Total Vendors" value={summary.total_vendors} tone="slate"/>
            <StatCard label="High Risk" value={summary.total_high_risk} tone="red"/>
            <StatCard label="Documents on File" value={docs.length} tone="cyan"/>
          </div>
        )}

        {summary?.vendors?.length > 0 && (
          <div className="rugged-card p-4">
            <h3 className="font-black uppercase tracking-widest text-sm mb-3">Vendor Risk Board</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {summary.vendors.map(v => (
                <button key={v.vendor_id} onClick={() => setSelected(selected === v.vendor_id ? "" : v.vendor_id)}
                  data-testid={`vendor-row-${v.vendor_id}`}
                  className={`p-3 border-2 text-left transition ${
                    selected === v.vendor_id ? "border-cyan-500 bg-cyan-50" : "border-slate-200 hover:border-slate-400 bg-white"
                  }`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-black text-sm truncate">{v.vendor_name}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {v.doc_count} docs · {v.has_coi ? "COI ✓" : "COI ✗"} · {v.has_w9 ? "W9 ✓" : "W9 ✗"}
                      </div>
                    </div>
                    <span className={`px-2 h-6 leading-6 font-black uppercase tracking-widest text-[9px] ${
                      v.risk === "high" ? "bg-red-600 text-white" :
                      v.risk === "medium" ? "bg-amber-500 text-white" : "bg-emerald-500 text-white"
                    }`}>{v.risk}</span>
                  </div>
                  {(v.expiring_soon > 0 || v.expired > 0) && (
                    <div className="mt-1 text-[10px] text-red-700 font-bold">
                      {v.expired > 0 && `${v.expired} expired`}
                      {v.expired > 0 && v.expiring_soon > 0 && " · "}
                      {v.expiring_soon > 0 && `${v.expiring_soon} expiring ≤30d`}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="rugged-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-black uppercase tracking-widest text-sm">
              {selected ? `Documents · ${vendors.find(v => (v.id || v._id) === selected)?.name || "Vendor"}` : "All Documents"}
            </h3>
            {selected && (
              <button onClick={() => setSelected("")} data-testid="clear-vendor-filter"
                className="text-[10px] font-black uppercase tracking-widest underline">Clear filter</button>
            )}
          </div>
          {filtered.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No documents yet. Upload one to start tracking.</div>
          ) : (
            <div className="space-y-2">
              {filtered.map(d => (
                <div key={d.id} className="p-3 border-2 border-slate-200 flex items-center gap-3 flex-wrap" data-testid={`doc-row-${d.id}`}>
                  <FileText size={20} className="text-slate-500 shrink-0"/>
                  <div className="min-w-0 flex-1">
                    <div className="font-black text-sm">{KIND_LABEL[d.kind] || d.kind}</div>
                    <div className="text-[10px] text-slate-500">
                      {d.vendor_name} · {d.carrier || "no carrier"} · Policy {d.policy_number || "—"}
                      {d.coverage_amount_cents > 0 && ` · $${(d.coverage_amount_cents/100).toLocaleString()} coverage`}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`px-2 h-6 leading-6 font-black uppercase tracking-widest text-[9px] ${STATUS_BADGE[d.status]}`}>
                      {d.status.replace("_", " ")}
                    </span>
                    <div className="text-[10px] text-slate-600 mt-0.5">
                      {d.expires || "no expiry"}
                      {d.days_until !== null && d.days_until !== undefined && (
                        <span className={d.days_until < 0 ? "text-red-600 font-bold ml-1" : "ml-1"}>
                          ({d.days_until < 0 ? `${-d.days_until}d ago` : `in ${d.days_until}d`})
                        </span>
                      )}
                    </div>
                  </div>
                  {d.doc_url && (
                    <a href={d.doc_url} target="_blank" rel="noopener noreferrer" data-testid={`doc-view-${d.id}`}
                      className="h-8 px-2 border-2 border-slate-900 hover:bg-slate-50 font-black uppercase tracking-widest text-[10px] inline-flex items-center">
                      View
                    </a>
                  )}
                  <button onClick={() => del(d.id)} data-testid={`doc-delete-${d.id}`}
                    className="h-8 w-8 border-2 border-red-600 text-red-600 hover:bg-red-50 inline-flex items-center justify-center">
                    <Trash size={14}/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {showNew && (
          <UploadDocModal vendors={vendors} onClose={() => setShowNew(false)}
            onSaved={() => { setShowNew(false); loadAll(); }}/>
        )}
      </div>
    </AdminShell>
  );
}

function StatCard({ label, value, tone }) {
  const toneMap = {
    slate: "bg-slate-900 text-white",
    red: "bg-red-600 text-white",
    cyan: "bg-cyan-500 text-white",
  };
  return (
    <div className={`p-4 ${toneMap[tone]} shadow-[3px_3px_0_#0f172a]`} data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="text-[10px] font-black uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-4xl font-black mt-1">{value}</div>
    </div>
  );
}

function UploadDocModal({ vendors, onClose, onSaved }) {
  const [form, setForm] = useState({
    vendor_id: "", kind: "coi_general_liability", doc_url: "",
    policy_number: "", carrier: "", coverage_amount_cents: 0,
    expires: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.vendor_id) return toast.error("Select a vendor");
    setBusy(true);
    try {
      await api.post("/vendor-compliance/docs", {
        ...form,
        coverage_amount_cents: Math.round((parseFloat(form.coverage_amount_cents) || 0) * 100),
      });
      toast.success("Document saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4" data-testid="upload-doc-modal">
      <div className="bg-white w-full max-w-lg border-4 border-slate-900 shadow-[6px_6px_0_#0f172a] max-h-[90vh] overflow-y-auto">
        <div className="p-3 bg-emerald-600 text-white flex items-start justify-between">
          <div className="font-black uppercase tracking-widest">Upload Vendor Document</div>
          <button onClick={onClose} data-testid="upload-doc-close" className="p-1 hover:bg-emerald-700"><X size={18}/></button>
        </div>
        <div className="p-4 space-y-2">
          <select value={form.vendor_id} onChange={e => setForm({...form, vendor_id: e.target.value})} data-testid="doc-vendor-select"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm">
            <option value="">— Select vendor —</option>
            {vendors.map(v => <option key={v.id || v._id} value={v.id || v._id}>{v.name}</option>)}
          </select>
          <select value={form.kind} onChange={e => setForm({...form, kind: e.target.value})} data-testid="doc-kind-select"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm">
            {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <input value={form.doc_url} onChange={e => setForm({...form, doc_url: e.target.value})} placeholder="Document URL (Google Drive, Dropbox, etc.)"
            data-testid="doc-url"
            className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <div className="grid grid-cols-2 gap-2">
            <input value={form.carrier} onChange={e => setForm({...form, carrier: e.target.value})} placeholder="Carrier"
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            <input value={form.policy_number} onChange={e => setForm({...form, policy_number: e.target.value})} placeholder="Policy #"
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input value={form.coverage_amount_cents} onChange={e => setForm({...form, coverage_amount_cents: e.target.value})} placeholder="Coverage ($)" type="number"
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            <input value={form.expires} onChange={e => setForm({...form, expires: e.target.value})} placeholder="Expires (YYYY-MM-DD)" data-testid="doc-expires"
              className="h-10 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} placeholder="Notes"
            className="w-full p-2 border-2 border-slate-300 text-sm"/>
          <button onClick={save} disabled={busy || !form.vendor_id} data-testid="doc-save-btn"
            className="w-full h-11 bg-emerald-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Saving…" : "Save Document"}
          </button>
        </div>
      </div>
    </div>
  );
}
